import React, { useState, useEffect } from 'react';
import { Dialog } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import Button from '../common/Button';
import { useDispatch } from 'react-redux';
import { updateClient } from '../../store/slices/clientsSlice';
import SearchableDropdown from '../common/SearchableDropdown';
import { US_STATES } from '../../data/locationData';
import { PRIORITIZED_CITIES } from '../../data/cityData';
import { showToast } from '../utils/Toast';
import EmailTagInput from '../common/EmailTagInput';

function ClientDetailsDialog({ isOpen, onClose, client }) {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [isMaximized, setIsMaximized] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    emails: [], // Array for EmailTagInput
    cellPhone: '',
    altPhone: '',
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: ''
    },
    mapLink: ''
  });
  
  const [editableFields, setEditableFields] = useState({
    city: false,
    state: false
  });

  // Initialize form data when client data changes
  useEffect(() => {
    if (client) {
      // Parse email string into array if it contains commas
      const emailString = client.email || '';
      const emailArray = emailString.split(',').map(email => email.trim()).filter(Boolean);
      
      setFormData({
        name: client.name || '',
        email: client.email || '',
        emails: emailArray,
        cellPhone: client.cellPhone || '',
        altPhone: client.altPhone || '',
        address: {
          street: client.address?.street || '',
          city: client.address?.city || '',
          state: client.address?.state || '',
          zipCode: client.address?.zipCode || ''
        },
        mapLink: client.mapLink || ''
      });
    }
  }, [client]);

  // Return early if no client data is available
  if (!client) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name.startsWith('address.')) {
      const addressField = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        address: {
          ...prev.address,
          [addressField]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      await dispatch(updateClient({ 
        id: client._id, 
        updates: formData 
      })).unwrap();
      
      // Use showToast if it's available (like in NewLead.js), or silently close the dialog
      if (typeof showToast === 'function') {
        showToast('Client updated successfully', 'success');
      }
      
      onClose();
    } catch (error) {
      console.error('Failed to update client:', error);
      
      // Use showToast if it's available, otherwise fall back to alert
      if (typeof showToast === 'function') {
        showToast(error?.message || 'Failed to update client', 'error');
      } else {
        alert(error?.message || 'Failed to update client');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    onClose();
  };
  
  // Toggle maximize/minimize
  const toggleMaximize = () => {
    setIsMaximized(!isMaximized);
  };

  const inputClasses = `
    w-full border border-gray-200 px-4 py-2.5 
    focus:outline-none focus:border-blue-500 text-gray-900
    bg-white shadow-sm focus:ring-blue-500 focus:border-blue-500 block sm:text-sm border-gray-300 rounded-md
  `;

  const textAreaClasses = `
    w-full border border-gray-200 px-4 py-2.5 
    focus:outline-none focus:border-blue-500 text-gray-900
    bg-white resize-none h-24 shadow-sm focus:ring-blue-500 block sm:text-sm border-gray-300 rounded-md
  `;

  const labelClasses = "block text-xs uppercase text-gray-500 mb-1 flex items-center gap-1";

  return (
    <Dialog 
      open={isOpen} 
      onClose={onClose}
      className="relative z-50"
    >
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      <div className="fixed inset-0">
        {/* Full screen dialog */}
        <div className={`fixed inset-0 z-50 overflow-hidden bg-white ${isMaximized ? '' : 'mx-auto my-16 max-w-7xl rounded-lg shadow-xl'}`}>
          {/* Dialog header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <Dialog.Title className="text-lg font-semibold text-gray-900">
              Client Details
            </Dialog.Title>
            <div className="flex items-center space-x-2">
              {/* Maximize/restore icon */}
              <button 
                className="text-gray-500 hover:text-gray-700 p-2 rounded-full hover:bg-gray-100"
                onClick={toggleMaximize}
                title={isMaximized ? "Restore dialog size" : "Maximize dialog"}
              >
                {isMaximized ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 3v3a2 2 0 01-2 2H3m18 0h-3a2 2 0 01-2-2V3m0 18v-3a2 2 0 012-2h3M3 16h3a2 2 0 012 2v3" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                  </svg>
                )}
              </button>
              
              {/* Close icon */}
              <button 
                className="text-gray-400 hover:text-gray-500"
                onClick={onClose}
              >
                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>
          </div>

          {/* Dialog content - scrollable area */}
          <div className="h-[calc(100vh-64px)] overflow-y-auto">
            <div className="p-6 space-y-10">
              {/* Client Information Section */}
              <div>
                <h2 className="text-lg font-medium mb-4 text-gray-800 border-b pb-2">Client Information</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-y-6 gap-x-8">
                  <div>
                    <label className={labelClasses}>Name</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className={inputClasses}
                      placeholder="Enter client name"
                    />
                  </div>
                  <div>
                    <div className="flex items-center justify-between">
                      <label className={labelClasses}>Email</label>
                      <span className="text-xs text-gray-500">Type email and press Enter or comma to add</span>
                    </div>
                    <EmailTagInput
                      emails={formData.emails}
                      onChange={(emails) => {
                        setFormData(prev => ({
                          ...prev,
                          emails: emails,
                          email: emails.join(', ') // Keep email field in sync for backward compatibility
                        }));
                      }}
                      placeholder="Enter email address(es)"
                      className={inputClasses}
                    />
                  </div>
                  <div>
                    <label className={labelClasses}>Cell Phone</label>
                    <input
                      type="text"
                      name="cellPhone"
                      value={formData.cellPhone}
                      onChange={handleChange}
                      className={inputClasses}
                      placeholder="(000) 000-0000"
                    />
                  </div>
                  <div>
                    <label className={labelClasses}>Alt Phone</label>
                    <input
                      type="text"
                      name="altPhone"
                      value={formData.altPhone}
                      onChange={handleChange}
                      className={inputClasses}
                      placeholder="(000) 000-0000"
                    />
                  </div>
                </div>
              </div>

              {/* Address Section */}
              <div>
                <h2 className="text-lg font-medium mb-4 text-gray-800 border-b pb-2">Address</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                  <div className="col-span-1 md:col-span-2">
                    <label className={labelClasses}>Street</label>
                    <textarea
                      name="address.street"
                      value={formData.address.street}
                      onChange={handleChange}
                      className={textAreaClasses}
                      placeholder="Enter client address"
                    />
                  </div>
                  <div>
                    <label className={labelClasses}>City</label>
                    <SearchableDropdown
                      key="client-city-dropdown"
                      options={PRIORITIZED_CITIES}
                      value={formData.address.city}
                      onChange={(value) => setFormData({
                        ...formData,
                        address: {
                          ...formData.address,
                          city: value
                        }
                      })}
                      placeholder="Search city..."
                      className={inputClasses}
                      onToggleEdit={(isEditable) => setEditableFields(prev => ({...prev, city: isEditable}))}
                      isEditable={editableFields.city}
                    />
                  </div>
                  <div>
                    <label className={labelClasses}>State</label>
                    <SearchableDropdown
                      key="client-state-dropdown"
                      options={US_STATES}
                      value={formData.address.state}
                      onChange={(value) => setFormData({
                        ...formData,
                        address: {
                          ...formData.address,
                          state: value
                        }
                      })}
                      placeholder="Search state..."
                      className={inputClasses}
                      onToggleEdit={(isEditable) => setEditableFields(prev => ({...prev, state: isEditable}))}
                      isEditable={editableFields.state}
                    />
                  </div>
                  <div>
                    <label className={labelClasses}>Zip Code</label>
                    <input
                      type="text"
                      name="address.zipCode"
                      value={formData.address.zipCode}
                      onChange={handleChange}
                      className={inputClasses}
                      placeholder="Enter zip code"
                    />
                  </div>
                  <div className="col-span-1 md:col-span-2">
                    <label className={labelClasses}>Map Link</label>
                    <input
                      type="text"
                      name="mapLink"
                      value={formData.mapLink}
                      onChange={handleChange}
                      placeholder="e.g. https://maps.google.com/?q=..."
                      className={inputClasses}
                    />
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex justify-end mt-6 space-x-3">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleCancel}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={handleSave}
                  disabled={loading}
                >
                  {loading ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
}

export default ClientDetailsDialog; 